import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Eye, EyeOff } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminApi";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login");
  const [code, setCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();
  const forgot = mode === "forgot";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const creds = { email: email.trim(), password: password.trim() };
      if (forgot) await adminApi.post("/auth/reset-password", { email: creds.email, code: code.trim(), new_password: creds.password });
      else await adminApi.post("/auth/login", creds);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#0A0A0A] border border-[#222] p-8 animate-slide-up">
        <Link to="/" className="font-heading text-2xl font-light">
          EZ <span className="gold-gradient-text italic">Capelli</span>
        </Link>
        <div className="flex items-center gap-2 mt-2 mb-8">
          <Lock size={14} className="text-[#D4AF37]" />
          <span className="overline">{forgot ? "Recuperar senha" : "Acesso ao painel"}</span>
        </div>
        <label className="text-xs text-white/50">E-mail</label>
        <input
          data-testid="admin-login-email"
          type="email"
          autoCapitalize="none"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-1 mb-5 bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-sm px-4 py-2.5 transition-colors"
        />
        {forgot && (
          <>
            <label className="text-xs text-white/50">Código de recuperação</label>
            <input
              data-testid="admin-reset-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              className="w-full mt-1 mb-5 bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-sm px-4 py-2.5 transition-colors font-mono"
            />
          </>
        )}
        <label className="text-xs text-white/50">{forgot ? "Nova senha (mínimo 10 caracteres)" : "Senha"}</label>
        <div className="relative mt-1 mb-5">
          <input
            data-testid="admin-login-password"
            type={showPw ? "text" : "password"}
            required
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-sm pl-4 pr-11 py-2.5 transition-colors"
          />
          <button
            type="button"
            data-testid="admin-login-toggle-password"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
            className="absolute right-0 top-0 h-full px-3 text-white/50 hover:text-[#D4AF37] transition-colors"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="text-rose-400 text-xs mb-4">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="w-full bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-50 text-[#050505] text-sm font-semibold py-3 transition-colors"
        >
          {busy ? "Aguarde..." : forgot ? "Redefinir senha e entrar" : "Entrar"}
        </button>
        <button
          type="button"
          data-testid="admin-forgot-toggle"
          onClick={() => { setMode(forgot ? "login" : "forgot"); setError(""); }}
          className="w-full text-xs text-white/50 hover:text-[#D4AF37] mt-4 transition-colors"
        >
          {forgot ? "Voltar ao login" : "Esqueci minha senha"}
        </button>
        {forgot && (
          <p className="text-[11px] text-white/40 mt-3">
            Use um dos códigos de recuperação gerados em Painel → Segurança. Cada código funciona uma única vez.
          </p>
        )}
      </form>
    </div>
  );
}
